if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.addOnVariantSelects.forEach((select) =>
          select.addEventListener('change', this.onAddOnVariantChange.bind(this))
        );
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        const selectedAddOns = this.addOnVariantInputs.filter((input) => input.checked && !input.disabled && input.value);
        const mainVariantId = Number(formData.get('id'));

        if (selectedAddOns.length > 0 && mainVariantId) {
          config.headers['Content-Type'] = 'application/json';
          config.body = JSON.stringify(this.buildAddOnRequestBody(formData, selectedAddOns));
        } else {
          config.body = formData;
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(this.normalizeCartResponse(response));
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(this.normalizeCartResponse(response));
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      buildAddOnRequestBody(formData, selectedAddOns) {
        const mainItem = {
          id: Number(formData.get('id')),
          quantity: Number(formData.get('quantity') || 1),
        };
        const properties = this.getLineItemProperties(formData);
        const sellingPlan = formData.get('selling_plan');

        if (Object.keys(properties).length > 0) mainItem.properties = properties;
        if (sellingPlan) mainItem.selling_plan = sellingPlan;

        const body = {
          items: [
            mainItem,
            ...selectedAddOns.map((input) => ({
              id: Number(input.value),
              quantity: Number(input.dataset.addOnQuantity || 1),
            })),
          ],
        };

        if (this.cart) {
          body.sections = this.cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
        }

        return body;
      }

      getLineItemProperties(formData) {
        const properties = {};

        for (const [key, value] of formData.entries()) {
          const propertyMatch = key.match(/^properties\[(.+)\]$/);
          if (propertyMatch && value !== '') properties[propertyMatch[1]] = value;
        }

        return properties;
      }

      normalizeCartResponse(response) {
        const firstAddedItem = response.items?.[0];

        if (firstAddedItem) {
          response.id = response.id || firstAddedItem.id;
          response.key = response.key || firstAddedItem.key;
        }

        return response;
      }

      onAddOnVariantChange(event) {
        const select = event.currentTarget;
        const selectedOption = select.options[select.selectedIndex];
        const item = select.closest('.tt-additional-products__item');
        const input = item?.querySelector('[data-add-on-variant-id]');
        const price = item?.querySelector('[data-add-on-price]');
        const image = item?.querySelector('.tt-additional-products__media img');

        if (!selectedOption || !input) return;

        input.value = selectedOption.value;
        input.disabled = selectedOption.dataset.available !== 'true';

        if (price && selectedOption.dataset.price) price.textContent = selectedOption.dataset.price;
        if (image && selectedOption.dataset.imageSrc) {
          image.src = selectedOption.dataset.imageSrc;
          if (selectedOption.dataset.imageSrcset) image.srcset = selectedOption.dataset.imageSrcset;
          image.alt = selectedOption.dataset.imageAlt || '';
        }
        item?.classList.toggle('tt-additional-products__item--unavailable', input.disabled);
      }

      get addOnVariantInputs() {
        return Array.from(document.querySelectorAll('[data-add-on-variant-id]')).filter((input) => input.form === this.form);
      }

      get addOnVariantSelects() {
        return Array.from(document.querySelectorAll('[data-add-on-variant-select]')).filter((select) => select.form === this.form);
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
